import { config, type UserConfigStore } from '@littlebird/sdk';

import { FEATURES, hasFeature } from '@/features/feature-flags';
import { isMac } from '@/utils/capabilities';

import type {
  MeetingTranscriptionEnginePreferenceSource,
  PreferredTranscriptionEngine,
} from '@electron/shared/meetings.types';

export function normalizeMeetingTranscriptionEnginePreferenceSource(
  raw: unknown
): MeetingTranscriptionEnginePreferenceSource {
  return raw === 'manual' ? 'manual' : 'default_policy';
}

export function normalizePreferredMeetingTranscriptionEngine(
  raw: unknown
): PreferredTranscriptionEngine {
  return raw === 'local' ? 'local' : 'remote';
}

export function getTranscriptionEnginePreferenceSource(): MeetingTranscriptionEnginePreferenceSource {
  return normalizeMeetingTranscriptionEnginePreferenceSource(
    config.meetings.transcription_engine_preference_source
  );
}

function resolveMeetingTranscriptionDefaultEngine(): PreferredTranscriptionEngine {
  return hasFeature(FEATURES.LOCAL_TRANSCRIPTION_DEFAULT) && isMac()
    ? 'local'
    : 'remote';
}

/**
 * Resolves the effective preferred transcription engine, honoring the
 * LOCAL_TRANSCRIPTION capability kill switch, a stored manual pick, and the
 * default-policy fallback. Reads from the shared `config` accessor, so it
 * falls back to schema defaults when the SDK isn't ready yet.
 */
export function resolvePreferredTranscriptionEngine(): PreferredTranscriptionEngine {
  if (!hasFeature(FEATURES.LOCAL_TRANSCRIPTION)) {
    return 'remote';
  }
  if (getTranscriptionEnginePreferenceSource() === 'manual') {
    return normalizePreferredMeetingTranscriptionEngine(
      config.meetings.preferred_transcription_engine
    );
  }
  return resolveMeetingTranscriptionDefaultEngine();
}

/**
 * Atomically records a manual engine pick by patching both the engine and its
 * preference source in a single transaction.
 */
export function setPreferredTranscriptionEngine(
  uc: UserConfigStore | null,
  engine: PreferredTranscriptionEngine
): void {
  uc?.meetings.patch({
    preferred_transcription_engine: engine,
    transcription_engine_preference_source: 'manual',
  });
}
